// ─── Guided Setup: watched zones ──────────────────────────────────────────────
//
// detector_config.zone_watch. SEVERAL zones per camera, each named, each with its
// own trigger and its own hours — because one camera genuinely carries more than
// one question:
//
//   a perimeter camera   a LINE at the kerb nobody should cross, and an AREA over
//                        the strip behind it nobody should be standing in
//   a corridor camera    one AREA per flat door
//
// The three triggers are not three thresholds of one rule. They answer different
// questions, and picking the wrong one produces a rule that can never fire:
//
//   CROSSING   a thief crosses a perimeter in two seconds. A waiting time would
//              never be reached, so there isn't one.
//   PRESENCE   nobody should be in a planting strip at 2am at all. Being there
//              IS the event.
//   STAYING    at a flat door or in a basement aisle, being present is perfectly
//              normal and staying is not. Only this one counts time.
//
// The name is what reaches the alert. "Someone at Flat 402" is something a guard
// can act on; "intrusion on camera 3" is not.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import PatrolZoneEditor from '../PatrolZoneEditor';
import { Field, Panel, Choice, SettingRow, Advanced, countChanged, useModuleSaver }
  from './SetupBits';

const DETECTOR = 'zone_watch';

const NIGHT_DEF = { start: '00:00', end: '05:30' };

const TRIGGERS = [
  { v: 'crossing', kind: 'line', icon: '↔️', title: 'Crossing a line',
    sub: 'Nobody should cross it — a kerb, a path, a boundary' },
  { v: 'presence', kind: 'area', icon: '⬛', title: 'Being there at all',
    sub: 'Nobody belongs inside — a planting strip, a roof' },
  { v: 'dwell', kind: 'area', icon: '⏱️', title: 'Staying too long',
    sub: 'Being there is normal; staying is not — a flat door, a basement aisle' },
];

// `def` matches ZoneWatchDetector's own defaults, so Advanced shows a true
// "changed" count rather than counting the defaults themselves.
const ADV_DWELL = [
  { key: 'dwell_secs', label: 'Seconds before it alerts', type: 'int',
    min: 0, max: 1800, step: 10, def: 120,
    hint: 'Two minutes at a flat door: long enough that somebody unlocking and going in never reaches it, short enough to catch somebody working at the lock. Zero switches the daytime rule off — right for anywhere residents use normally in daylight, like a perimeter strip.' },
  { key: 'night_dwell_secs', label: 'Seconds before it alerts, at night', type: 'int',
    min: 0, max: 900, step: 5, def: 120,
    hint: 'Same two minutes by default. It is tempting to make this much shorter, but residents do come home at 1am and take half a minute at their own door — a short bar alerts on them every time. Anywhere nobody belongs at all, use the "being there at all" trigger instead of a short waiting time.' },
  { key: 'occlusion_grace_secs', label: 'Seconds out of sight before the clock pauses', type: 'int',
    min: 1, max: 120, step: 5, def: 10,
    hint: 'Someone walking in front of them should not restart the clock. Raise it in a basement, where parked cars hide people for much longer — raising it here also holds the visit open at least that long.' },
  { key: 'visit_gap_secs', label: 'Seconds away before it counts as a new visit', type: 'int',
    min: 5, max: 600, step: 5, def: 10,
    hint: 'Gone longer than this and they start again from zero when they come back. Ten seconds covers stepping aside; raise it and ordinary errands join up — somebody carrying shopping up in four trips becomes one long stay. It is never allowed below the out-of-sight setting above, on this zone or any other on the camera, because a gap cannot both count as presence and end the visit.' },
  { key: 'edge_release_secs', label: 'Seconds outside before they have left', type: 'int',
    min: 0, max: 120, step: 5, def: 20,
    hint: 'A person standing on the edge of the shape drifts in and out of it. Without this, one person reads as a string of separate visits.' },
  { key: 'cooldown_secs', label: 'Quiet period after an alert (s)', type: 'int',
    min: 0, max: 3600, step: 30, def: 600 },
];

const ADV_PRESENCE = [
  { key: 'persist_frames', label: 'Frames before it counts', type: 'int',
    min: 1, max: 15, step: 1, def: 3 },
  { key: 'cooldown_secs', label: 'Quiet period after an alert (s)', type: 'int',
    min: 0, max: 3600, step: 30, def: 60 },
];

const ADV_CROSSING = [
  { key: 'cooldown_secs', label: 'Quiet period after an alert (s)', type: 'int',
    min: 0, max: 3600, step: 10, def: 20 },
];

const advFor = t => (t === 'dwell' ? ADV_DWELL : t === 'crossing' ? ADV_CROSSING : ADV_PRESENCE);

const blankZone = (trigger = 'dwell') => ({
  name: '', trigger, kind: TRIGGERS.find(t => t.v === trigger).kind,
  polygon: null, line: null, direction: 'both',
  night_mode: 'inherit', night_window: { ...NIGHT_DEF },
  ...Object.fromEntries(advFor(trigger).map(s => [s.key, s.def])),
});

const hhmm = w => `${w?.start || NIGHT_DEF.start}–${w?.end || NIGHT_DEF.end}`;

// What this zone will actually do tonight, in one line. Shown on every row
// because "follows the camera" on its own tells an installer nothing about the
// hours the zone is really armed for.
function describe(z, camNight) {
  const win = z.night_mode === 'custom' ? z.night_window : camNight;
  const hrs = z.night_mode === 'off' ? 'round the clock' : hhmm(win);
  if (z.trigger === 'crossing') {
    const d = z.direction === 'both' ? 'either way' : `one way (${z.direction})`;
    return `Crossing, ${d} · ${hrs}`;
  }
  if (z.trigger === 'presence') return `Anyone inside · ${hrs}`;
  // Armed round the clock means there is no night, so there is one bar. Printing
  // "120s at night" for a number the detector never reads is worse than printing
  // nothing: it reads as configured behaviour.
  if (z.night_mode === 'off') {
    return Number(z.dwell_secs) > 0 ? `${z.dwell_secs}s · round the clock`
                                    : 'Nothing set — it cannot alert';
  }
  const day = Number(z.dwell_secs) > 0 ? `${z.dwell_secs}s by day` : 'off by day';
  return `${z.night_dwell_secs}s at night (${hrs}) · ${day}`;
}

const isDrawn = z => (z.kind === 'line'
  ? (z.line || []).length === 2
  : (z.polygon || []).length >= 3);

export default function ZoneWatchPanel({ cameras, onChanged }) {
  const [cams, setCams] = useState(null);
  const [open, setOpen] = useState(null);   // camera idx being configured
  const [edit, setEdit] = useState(null);   // { at: index|null, ...zone }
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(() => apiFetch('/ui/cameras')
    .then(r => (r.ok ? r.json() : null))
    .then(d => { if (d) setCams(d.cameras || []); })
    .catch(() => setErr('Could not read the cameras.')), [setErr]);
  useEffect(() => { load(); }, [load]);

  const camOf = idx => (cams || []).find(c => c.idx === idx);
  const cfgOf = idx => (camOf(idx)?.detector_config || {})[DETECTOR] || {};
  const zonesOf = idx => cfgOf(idx).zones || [];
  const nightOf = idx => cfgOf(idx).night_window || NIGHT_DEF;

  async function saveCfg(idx, cfg, what, withDetector, camCfg) {
    const body = { detector_config: { [DETECTOR]: cfg } };
    if (withDetector) {
      body.detectors = [...new Set([...(camOf(idx)?.detectors || []), DETECTOR])];
    }
    // cam_config merges key by key on the camera PUT, so sending one key cannot
    // clobber the exclusion zones drawn on the same camera.
    if (camCfg) body.cam_config = camCfg;
    const d = await run('PUT', `/cameras/${idx}`, body, what);
    if (d) await load();
    return d;
  }

  const saveZones = (idx, zones, what, camCfg) =>
    saveCfg(idx, { ...cfgOf(idx), night_window: nightOf(idx), zones }, what, true, camCfg);

  // A waiting-time zone cannot fire while the swaying-plant filter is on, so the
  // first one switches it off for the camera rather than leaving a zone that
  // looks configured and never alerts. Said out loud in the saved-message and on
  // the zone list afterwards — the flag is CAMERA-wide, so it is also switched
  // off over parts of the view no zone covers.
  //
  // Not done when the camera runs another detector as well: that detector shares
  // the same person stream and alerts on anything anywhere in frame, so dropping
  // foliage rejection for it is a decision somebody should make deliberately.
  // That case keeps the warning panel instead.
  const otherDetectors = idx =>
    (camOf(idx)?.detectors || []).some(d => d !== DETECTOR);

  const autoOscOff = (idx, zones) =>
    zones.some(z => z.trigger === 'dwell')
    && (camOf(idx)?.cam_config || {}).osc_filter !== false
    && !otherDetectors(idx);

  async function saveZone() {
    const { at, ...z } = edit;
    z.name = (z.name || '').trim() || `Zone ${zonesOf(open).length + 1}`;
    if (z.kind === 'line') z.polygon = null; else z.line = null;
    const list = [...zonesOf(open)];
    if (at === null) list.push(z); else list[at] = z;
    const auto = autoOscOff(open, list);
    const what = auto
      ? 'this zone, and switched the swaying-plant filter off for this camera'
      : 'this zone';
    if (await saveZones(open, list, what, auto ? { osc_filter: false } : null)) {
      setEdit(null);
    }
  }

  async function removeZone(i) {
    if (!window.confirm('Remove this zone? Its drawing goes with it.')) return;
    await saveZones(open, zonesOf(open).filter((_, n) => n !== i), 'the change');
  }

  async function stopCamera(idx) {
    if (!window.confirm('Stop watching on this camera? The zones are kept, the alerting stops.')) return;
    const dets = (camOf(idx)?.detectors || []).filter(d => d !== DETECTOR);
    if (await run('PUT', `/cameras/${idx}`, { detectors: dets }, 'the change')) await load();
  }

  // A dwell zone cannot fire while the oscillation filter is on for the camera:
  // the filter reads somebody standing still as a swaying plant, drops the track,
  // and the keepalive then lets the motion gate stop YOLO on them entirely. The
  // zone looks perfectly configured and never alerts, with nothing said anywhere.
  //
  // Not switched automatically. The flag changes what the camera REJECTS, and a
  // panel that quietly turns off foliage rejection because somebody drew a zone
  // is how an outdoor camera starts alerting on branches all night. Named,
  // explained, one click — the human still decides.
  const needsOscOff = idx => {
    const cam = camOf(idx);
    if (!cam) return false;
    const hasDwell = (zonesOf(idx) || []).some(z => z.trigger === 'dwell');
    return hasDwell && (cam.cam_config || {}).osc_filter !== false;
  };

  const turnOscOff = async idx => {
    // cam_config merges key by key on the camera PUT, so this cannot clobber the
    // exclusion zones drawn on the same camera.
    if (await run('PUT', `/cameras/${idx}`,
                  { cam_config: { osc_filter: false } }, 'the foliage filter')) {
      await load();
    }
  };

  const setCamNight = (idx, patch) =>
    saveCfg(idx, { ...cfgOf(idx), night_window: { ...nightOf(idx), ...patch },
                   zones: zonesOf(idx) }, 'the hours', false);

  // ── one zone ──
  if (edit) {
    // The night bar is not read at all when the zone is armed round the clock, so
    // the row comes out rather than sitting there inviting a value that does
    // nothing. Same reason the summary line above stopped printing it.
    const adv = advFor(edit.trigger)
      .filter(sp => !(sp.key === 'night_dwell_secs' && edit.night_mode === 'off'));
    return (
      <>
        <div className="gs-section-title">
          {edit.at === null ? 'A new zone' : `Editing ${edit.name || 'this zone'}`} on {camOf(open)?.name}
        </div>

        <Field label="Name it"
               help="This is what the alert says, so name it the way somebody would say it out loud — Flat 402, Basement aisle B, West kerb.">
          <input className="gs-input" value={edit.name} maxLength={40}
                 placeholder="Flat 402"
                 onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} />
        </Field>

        <Field label="What should raise an alert?">
          <div className="gs-choices">
            {TRIGGERS.map(t => (
              <Choice key={t.v} icon={t.icon} title={t.title} sub={t.sub}
                      selected={edit.trigger === t.v}
                      onClick={() => setEdit(s => ({
                        ...s, trigger: t.v, kind: t.kind,
                        ...Object.fromEntries(advFor(t.v).map(x => [x.key, s[x.key] ?? x.def])),
                      }))} />
            ))}
          </div>
        </Field>

        {edit.kind === 'area' ? (
          <Field label="Draw the area"
                 help="Tap its corners. Somebody counts as inside when the MIDDLE of them is inside the shape, so draw it around where a person's body will be, not tight to the floor they stand on.">
            <PatrolZoneEditor cameraIdx={open} value={edit.polygon || []}
                              onChange={p => setEdit(s => ({ ...s, polygon: p }))} />
          </Field>
        ) : (
          <>
            <Field label="Draw the line" help="Tap the two ends of it.">
              <PatrolZoneEditor cameraIdx={open} shape="line" value={edit.line || []}
                                onChange={p => setEdit(s => ({ ...s, line: p }))} />
            </Field>
            <Field label="Which way counts?"
                   help="Both is usually right for a boundary: somebody leaving over a different stretch than the one they came in over is just as much an intrusion.">
              <div className="gs-choices">
                <Choice icon="↔️" title="Either way" sub="Both directions alert"
                        selected={edit.direction === 'both'}
                        onClick={() => setEdit(s => ({ ...s, direction: 'both' }))} />
                <Choice icon="➡️" title="One way"
                        sub="If alerts come when somebody walks the wrong way, switch this"
                        selected={edit.direction !== 'both'}
                        onClick={() => setEdit(s => ({ ...s, direction: s.direction === 'in' ? 'out' : 'in' }))} />
              </div>
            </Field>
          </>
        )}

        <Field label="When is it armed?"
               help={`Night for this camera is ${hhmm(nightOf(open))}. A zone follows that unless you give it its own hours.`}>
          <div className="gs-choices">
            <Choice icon="🌙" title="Night" sub={`Follows the camera · ${hhmm(nightOf(open))}`}
                    selected={edit.night_mode === 'inherit'}
                    onClick={() => setEdit(s => ({ ...s, night_mode: 'inherit' }))} />
            <Choice icon="🕐" title="Its own hours" sub="For a shop frontage or a different shift"
                    selected={edit.night_mode === 'custom'}
                    onClick={() => setEdit(s => ({ ...s, night_mode: 'custom' }))} />
            <Choice icon="☀️" title="All day" sub="Round the clock, no hours at all"
                    selected={edit.night_mode === 'off'}
                    onClick={() => setEdit(s => ({ ...s, night_mode: 'off' }))} />
          </div>
        </Field>

        {edit.night_mode === 'custom' && (
          <Field label="Its hours">
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <input className="gs-input" type="time"
                     value={edit.night_window?.start || NIGHT_DEF.start}
                     onChange={e => setEdit(s => ({ ...s, night_window: { ...s.night_window, start: e.target.value } }))} />
              <span className="gs-foot-note">to</span>
              <input className="gs-input" type="time"
                     value={edit.night_window?.end || NIGHT_DEF.end}
                     onChange={e => setEdit(s => ({ ...s, night_window: { ...s.night_window, end: e.target.value } }))} />
            </div>
          </Field>
        )}

        <Advanced changed={countChanged(adv, edit)}>
          {adv.map(s => (
            <SettingRow key={s.key} spec={s} value={edit[s.key]}
                        onChange={v => setEdit(e2 => ({ ...e2, [s.key]: v }))} />
          ))}
        </Advanced>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary" onClick={saveZone} disabled={busy || !isDrawn(edit)}>
            {busy ? <span className="gs-spin" /> : 'Save this zone'}
          </button>
          <button className="gs-btn ghost" onClick={() => { setEdit(null); setErr(null); }}>Cancel</button>
          {!isDrawn(edit) && (
            <span className="gs-foot-note">
              {edit.kind === 'line' ? 'Place both ends of the line'
                                    : 'Draw the area — three corners is enough'}
            </span>
          )}
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── one camera's zones ──
  if (open !== null) {
    const zones = zonesOf(open);
    const night = nightOf(open);
    return (
      <>
        <div className="gs-section-title">Zones on {camOf(open)?.name}</div>

        <Field label="When is night here?"
               help="Every zone on this camera follows these hours unless it is given its own.">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input className="gs-input" type="time" defaultValue={night.start} disabled={busy}
                   onBlur={e => e.target.value !== night.start && setCamNight(open, { start: e.target.value })} />
            <span className="gs-foot-note">to</span>
            <input className="gs-input" type="time" defaultValue={night.end} disabled={busy}
                   onBlur={e => e.target.value !== night.end && setCamNight(open, { end: e.target.value })} />
          </div>
        </Field>

        {zones.length === 0 && (
          <p className="gs-help">Nothing drawn yet. A camera with no zones raises nothing.</p>
        )}

        {(camOf(open)?.cam_config || {}).osc_filter === false && (
          <p className="gs-help">
            The swaying-plant filter is <b>off</b> for this camera, so it can see
            somebody standing still. That applies to the whole view, not only the
            zones — if anything here moves by itself, mark it as an ignore area.
            Detection Settings has the switch.
          </p>
        )}

        {needsOscOff(open) && (
          <Panel tone="bad" title="This camera cannot see somebody standing still">
            A zone here waits for somebody to STAY, but the swaying-plant filter is
            still on for this camera. That filter reads a person standing still as a
            plant: it drops them after about half a minute, and the camera then stops
            looking at them altogether — so the waiting time is never reached and no
            alert is ever raised.
            {' '}Switch it off here. Draw ignore areas over any real plants in the view
            first, or they become candidates.
            <div style={{ marginTop: 10 }}>
              <button className="gs-btn small" disabled={busy}
                      onClick={() => turnOscOff(open)}>
                {busy ? <span className="gs-spin" /> : 'Switch it off for this camera'}
              </button>
            </div>
          </Panel>
        )}

        {zones.map((z, i) => {
          const drawn = isDrawn(z);
          const t = TRIGGERS.find(x => x.v === z.trigger) || TRIGGERS[2];
          return (
            <div className={`gs-row${drawn ? '' : ' gs-row-bad'}`} key={i}>
              <span className="gs-row-icon">{drawn ? t.icon : '⚠️'}</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{z.name || `Zone ${i + 1}`}</span>
                <span className="gs-row-sub">
                  {drawn ? describe(z, night) : 'NOTHING DRAWN — it cannot alert'}
                </span>
              </span>
              <button className="gs-btn small"
                      onClick={() => { setErr(null); setEdit({ ...blankZone(z.trigger), ...z, at: i }); }}>
                Change
              </button>
              <button className="gs-btn small ghost" disabled={busy}
                      onClick={() => removeZone(i)}>Remove</button>
            </div>
          );
        })}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="gs-btn primary"
                  onClick={() => { setErr(null); setEdit({ ...blankZone(), at: null }); }}>
            Add a zone
          </button>
          <button className="gs-btn ghost" onClick={() => { setOpen(null); setErr(null); }}>Done</button>
        </div>
        {err && <div className="gs-err" role="alert">{err}</div>}
      </>
    );
  }

  // ── the camera list ──
  const rows = (cams || [])
    .filter(c => cameras.some(x => x.idx === c.idx))
    .map(c => ({
      idx: c.idx,
      name: c.name || `Camera ${c.idx}`,
      on: (c.detectors || []).includes(DETECTOR),
      zones: ((c.detector_config || {})[DETECTOR] || {}).zones || [],
    }));

  return (
    <>
      {!cams && !err && (
        <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>
      )}

      {cams && (
        <>
          <p className="gs-help">
            As many zones per camera as the view needs. A camera on the boundary usually
            carries two — a line at the kerb, and the strip behind it — and a corridor
            camera one per flat door.
          </p>

          {rows.map(r => {
            const bad = r.on && (r.zones.some(z => !isDrawn(z)) || !r.zones.length
                                 || needsOscOff(r.idx));
            return (
              <div className={`gs-row${bad ? ' gs-row-bad' : ''}`} key={r.idx}>
                <span className="gs-row-icon">{!r.on ? '📷' : bad ? '⚠️' : '🗺'}</span>
                <span className="gs-row-text">
                  <span className="gs-row-name">{r.name}</span>
                  <span className="gs-row-sub">
                    {!r.on ? 'Not watching anything here'
                      : needsOscOff(r.idx) ? 'A waiting-time zone here CANNOT ALERT — the swaying-plant filter is still on'
                      : !r.zones.length ? 'Switched on but NO ZONES — it cannot alert'
                      : r.zones.some(z => !isDrawn(z))
                        ? `${r.zones.length} zone(s), one of them not drawn`
                        : r.zones.map(z => z.name).filter(Boolean).join(' · ')
                          || `${r.zones.length} zone(s)`}
                  </span>
                </span>
                <button className="gs-btn small"
                        onClick={() => { setErr(null); setOpen(r.idx); }}>
                  {r.on ? 'Change' : 'Set up'}
                </button>
                {r.on && (
                  <button className="gs-btn small ghost" disabled={busy}
                          onClick={() => stopCamera(r.idx)}>Stop</button>
                )}
              </div>
            );
          })}

          <Panel title="Waiting-time zones switch off the swaying-plant filter">
            That filter rejects branches and washing by spotting that they move without
            going anywhere — and it reads a person standing still the same way, stopping
            seeing them within about half a minute, so a waiting time is never reached.
            The first zone here that waits for somebody to stay switches it off for that
            camera, and says so when it saves.
            {' '}It is not about being indoors: an open balcony corridor needs it off
            just as much as a sealed office. It applies to the whole view rather than
            the zone, so mark anything that moves by itself as an ignore area. Cameras
            with only crossing or presence zones keep it on.
          </Panel>

          <Panel title="What happens on an alert">
            The same as any other — photo, clip, and your alert devices. The alert names
            the zone, so it says which flat or which stretch rather than which camera.
          </Panel>

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
